// Talentspeicher. Jede Person der Talentliste belegt einen Platz, auch wenn sie
// mehrere Spaces hat. Person ist der owner eines Space. Einladungen ohne
// gestartetes Assessment haben noch keinen owner und zählen je Zeile. Rang
// nach dem createdAt des frühesten gezählten Space der Person. Die ersten N
// Personen sind voll sichtbar, ab N+1 sind alle Spaces der Person gesperrt,
// auch die, die selbst nicht zählen (etwa ATTRACT ohne mail_stored).
// Ergebnisse laden am owner, ein solcher Space zeigte sonst die Ergebnisse
// einer gesperrten Person.
// Nur Löschen gibt Platz frei, abgeschlossene oder abgesagte Talente zählen
// weiter. Reine Funktionen ohne helpers/index.js, sample-data.js importiert
// selbst nichts. Aivy OS kopiert die Vektoren aus __fixtures__/count-cases.json.

import { isSampleSpace } from '../../../helpers/sample-data'

// Dieselben Kontexte wie talentContextFilterInput (graphql/filter-inputs.js).
// EMPLOYEE sind Zugänge und zählen nicht.
const COUNTED_CONTEXTS = new Set(['SELECT', 'TALENTPOOL', 'ATTRACT'])

// ATTRACT-Spaces erscheinen erst mit mail_stored in der Talentliste
// (use-talents-query.js), vorher zählen sie auch nicht.
export const countsForTalentStorage = (space) =>
  !!space &&
  !space.soft_deleted &&
  COUNTED_CONTEXTS.has(space.context) &&
  (space.context !== 'ATTRACT' || !!space.mail_stored) &&
  !isSampleSpace(space) &&
  !space.is_demo

const isSet = (value) => value !== null && value !== undefined && value !== ''

/**
 * Key der Person, die ein Space belegt. Das ist der owner, ohne owner die
 * Space-ID. Gesetzt heißt weder null, undefined noch leer. Ohne beides null,
 * solche Spaces zählen nicht.
 */
export const talentUnitKeyOf = (space) => {
  if (!space) return null
  if (isSet(space.owner)) return space.owner
  return isSet(space.id) ? space.id : null
}

// createdAt ist stabil. updatedAt ändert sich bei Status, Reminder, Kommentar
// und Favorit und taugt nicht als Rang. Ohne gültiges createdAt landet ein
// Space am Ende. Gleichstand entscheidet zwischen Spaces die Space-ID und
// zwischen Personen ihr Key, beides per Code-Unit-Vergleich. So rechnen
// Dashboard und Aivy OS unabhängig von der Locale gleich.
const createdAtOf = (space) => {
  const time = Date.parse(space.createdAt)
  return Number.isFinite(time) ? time : Infinity
}

const compareIds = (a, b) => {
  const left = String(a)
  const right = String(b)
  if (left < right) return -1
  if (left > right) return 1
  return 0
}

const compareByTime = (leftTime, rightTime, leftId, rightId) => {
  if (leftTime !== rightTime) return leftTime < rightTime ? -1 : 1
  return compareIds(leftId, rightId)
}

const compareSpaces = (a, b) =>
  compareByTime(createdAtOf(a), createdAtOf(b), a.id, b.id)

const compareUnits = (a, b) => compareByTime(a.time, b.time, a.key, b.key)

// Spaces, die nicht zählen, aber zu einer gezählten Person gehören, teilen
// deren Rang und Sperre. Ausgenommen sind Beispieldaten und Demo, sie bleiben
// immer frei, und Gelöschte, die in keiner Liste stehen.
const sharesPersonLock = (space) =>
  !!space &&
  !space.soft_deleted &&
  isSet(space.id) &&
  isSet(space.owner) &&
  !isSampleSpace(space) &&
  !space.is_demo

/**
 * Index des Talentspeichers. Personen kommen in die Reihenfolge ihres
 * frühesten gezählten Space, bei Gleichstand entscheidet der Key der Person.
 * Doppelte Space-IDs zählen einmal.
 *
 * - orderedIds sind die gezählten Space-IDs, nach Person und innerhalb der
 *   Person nach createdAt
 * - rankById ordnet jeder Space-ID den Rang ihrer Person zu (ab 1), Spaces
 *   derselben Person teilen sich den Rang. Dazu kommen Spaces einer gezählten
 *   Person, die selbst nicht zählen (sharesPersonLock), hinter den gezählten.
 * - orderedUnits sind die Keys der Personen in Rangfolge
 * - rankByUnit ordnet jedem Key seinen Rang zu
 * - unitById ordnet jeder Space-ID aus rankById den Key ihrer Person zu
 * - used ist die Zahl der Personen
 */
export const rankTalentStorage = (spaces) => {
  const list = Array.isArray(spaces) ? spaces : []
  const counted = list.filter(countsForTalentStorage).sort(compareSpaces)

  const seenIds = new Set()
  const idsByUnit = new Map()
  const units = []

  // counted ist nach createdAt sortiert, der erste Space einer Person ist
  // also ihr frühester
  counted.forEach((space) => {
    const { id } = space
    if (!isSet(id) || seenIds.has(id)) return
    seenIds.add(id)

    const key = talentUnitKeyOf(space)
    if (!idsByUnit.has(key)) {
      idsByUnit.set(key, [])
      units.push({ key, time: createdAtOf(space) })
    }
    idsByUnit.get(key).push(id)
  })

  units.sort(compareUnits)

  const orderedIds = []
  const orderedUnits = []
  const rankById = new Map()
  const rankByUnit = new Map()
  const unitById = new Map()

  units.forEach(({ key }) => {
    orderedUnits.push(key)
    const rank = orderedUnits.length
    rankByUnit.set(key, rank)
    idsByUnit.get(key).forEach((id) => {
      orderedIds.push(id)
      rankById.set(id, rank)
      unitById.set(id, key)
    })
  })

  // Nicht gezählte Spaces einer gezählten Person, in der Reihenfolge der
  // Eingabe. Sie belegen keinen Platz und stehen nicht in orderedIds.
  list.forEach((space) => {
    if (!sharesPersonLock(space) || rankById.has(space.id)) return
    const rank = rankByUnit.get(space.owner)
    if (rank === undefined) return
    rankById.set(space.id, rank)
    unitById.set(space.id, space.owner)
  })

  return {
    orderedIds,
    rankById,
    orderedUnits,
    rankByUnit,
    unitById,
    used: orderedUnits.length
  }
}

// Einmal je Datenstand. Der Query-Cache liefert bei gleichen Daten dieselbe
// Array-Referenz, ein neuer Stand ist ein neues Array.
const rankCache = new WeakMap()

export const rankTalentStorageCached = (spaces) => {
  if (!Array.isArray(spaces)) return rankTalentStorage([])

  const cached = rankCache.get(spaces)
  if (cached) return cached

  const index = rankTalentStorage(spaces)
  rankCache.set(spaces, index)

  return index
}

// Sichtbare Plätze. Ohne gültiges Limit null, dann wird nichts gesperrt.
const visibleOf = (limit) =>
  typeof limit === 'number' && Number.isFinite(limit) && limit >= 0
    ? Math.floor(limit)
    : null

/**
 * Gesperrte Space-IDs, also alle Spaces der Personen ab Rang limit + 1, zuerst
 * die gezählten in der Reihenfolge von orderedIds, danach die nicht gezählten
 * derselben Personen aus rankById. Ohne rankById im Index zählt jede ID als
 * eigene Person, dann ist die Position in orderedIds der Rang.
 *
 * Ohne gültiges Limit wird nichts gesperrt (fail-open). Das betrifft null,
 * undefined, Infinity und alles, was keine Zahl ab 0 ist.
 */
export const lockedIdsFor = (index, limit) => {
  if (!index || !Array.isArray(index.orderedIds)) return new Set()
  const visible = visibleOf(limit)
  if (visible === null) return new Set()

  const ranks = index.rankById instanceof Map ? index.rankById : null
  const locked = new Set(
    index.orderedIds.filter((id, position) => {
      const rank = ranks ? ranks.get(id) : position + 1
      return typeof rank === 'number' && rank > visible
    })
  )

  if (ranks && ranks.size > index.orderedIds.length) {
    const counted = new Set(index.orderedIds)
    ranks.forEach((rank, id) => {
      if (!counted.has(id) && typeof rank === 'number' && rank > visible) {
        locked.add(id)
      }
    })
  }

  return locked
}

/**
 * Keys der gesperrten Personen, also ab Rang limit + 1. Für Prüfungen am
 * owner, etwa wenn ein Space gar nicht in den Daten steht (Mitarbeitende).
 * Ohne gültiges Limit leer (fail-open).
 */
export const lockedUnitsFor = (index, limit) => {
  const visible = visibleOf(limit)
  if (visible === null || !(index?.rankByUnit instanceof Map)) return new Set()

  const locked = new Set()
  index.rankByUnit.forEach((rank, key) => {
    if (rank > visible) locked.add(key)
  })
  return locked
}

// Gesperrte Zeilen behalten Name, Stelle, Datum, Status und Kommentare (Notizen
// des Kunden). Score, Matching und Zusatzangaben werden ersetzt, damit der
// echte Wert nicht im DOM steht. storage_rank ist der Rang der Person. Ohne
// Sperren bleibt die Referenz gleich.
export const applyTalentLocks = (rows, lockedIds, rankById) => {
  if (!Array.isArray(rows) || !lockedIds || lockedIds.size === 0) return rows

  return rows.map((row) => {
    if (!row || !lockedIds.has(row.id)) {
      return row ? { ...row, storage_locked: false } : row
    }

    return {
      ...row,
      storage_locked: true,
      storage_rank: rankById?.get(row.id) ?? null,
      score: '-',
      matching_score_to_space_career: null,
      additional_requested_data: null
    }
  })
}

const EMPTY_MAP = new Map()

const mapOf = (index, key) =>
  index?.[key] instanceof Map ? index[key] : EMPTY_MAP

/**
 * Zahl der heute sichtbaren Personen, die nach dem Wiederherstellen gesperrt
 * wären. Ein älterer Space kann seine Person nach vorn holen, auch ohne neuen
 * Platz zu brauchen. Dann rutscht die letzte sichtbare Person hinter das
 * Limit. Bei mehr als 0 blockt der Guard, die sichtbaren Personen bleiben so
 * beim Wiederherstellen immer dieselben.
 *
 * @param {object[]} spaces wiederherzustellende Spaces
 * @param {object[]} currentSpaces aktueller Stand (rohe Daten der Talentliste)
 * @param {number} limit Talentspeicher
 * @returns {number}
 */
export const talentRecoverLockedVisible = (spaces, currentSpaces, limit) => {
  const visible = visibleOf(limit)
  if (visible === null) return 0

  const current = Array.isArray(currentSpaces) ? currentSpaces : []
  const restored = (Array.isArray(spaces) ? spaces : [])
    .filter(Boolean)
    .map((space) => ({ ...space, soft_deleted: null }))
  if (!restored.length) return 0

  const restoredIds = new Set(restored.map(({ id }) => id))
  const before = rankTalentStorage(current)
  const after = rankTalentStorage(
    current.filter(({ id } = {}) => !restoredIds.has(id)).concat(restored)
  )
  const lockedAfter = lockedUnitsFor(after, limit)

  let count = 0
  before.rankByUnit.forEach((rank, key) => {
    if (rank <= visible && lockedAfter.has(key)) count += 1
  })
  return count
}

/**
 * Plätze, die ein Wiederherstellen belegen würde, also die Zahl neuer
 * Personen. Gedacht für gelöschte Spaces, gezählt wird so, als wäre
 * soft_deleted schon zurückgesetzt. Ein Space, der schon im Speicher liegt,
 * oder einer Person, die dort schon einen gezählten Space hat, belegt nichts.
 * Mehrere Spaces derselben neuen Person belegen einen Platz.
 *
 * @param {object[]} spaces wiederherzustellende Spaces
 * @param {object[]|object} [current] aktueller Stand, als Spaces oder als
 *   Index aus rankTalentStorage. Ohne gilt der Speicher als leer.
 * @returns {number}
 */
export const talentRecoverDelta = (spaces, current) => {
  const index = Array.isArray(current) ? rankTalentStorage(current) : current
  const rankById = mapOf(index, 'rankById')
  const rankByUnit = mapOf(index, 'rankByUnit')
  const added = new Set()

  ;(Array.isArray(spaces) ? spaces : []).forEach((space) => {
    if (!space) return
    const restored = { ...space, soft_deleted: null }
    if (!countsForTalentStorage(restored) || !isSet(restored.id)) return
    if (rankById.has(restored.id)) return

    const key = talentUnitKeyOf(restored)
    if (rankByUnit.has(key)) return
    added.add(key)
  })

  return added.size
}
